// ============================================================================
// SENTRY INTEGRATION MODULE
// ============================================================================
// This file contains all Sentry error tracking logic for the application.
// It handles:
// 1. Sentry initialization with environment detection
// 2. Angular $exceptionHandler integration with automatic user tracking
// 3. Centralized error reporting configuration
//
// Usage:
//   In entry.js: require('./path/to/sentryIntegration').init();
//   In app.js:   require('./path/to/sentryIntegration').setupAngular(app);
// ============================================================================
var enviroment = require('./enviroment.js');
module.exports = {
  
  // ==========================================================================
  // INITIALIZE SENTRY
  // ==========================================================================
  // Call this from entry.js AFTER Sentry CDN loads
  // This sets up the Sentry client with environment detection
  // ==========================================================================
  init: function() {
    
    // Check if Sentry was loaded from CDN
    if (typeof window.Sentry === 'undefined') {
      console.warn('⚠️ Sentry not loaded! Add the Sentry script tag to your HTML <head>');
      
      // Create a dummy Sentry object to prevent errors
      window.Sentry = {
        init: function() {},
        captureException: function() {},
        captureMessage: function() {},
        setUser: function() {},
        setTag: function() {},
        setExtra: function() {},
        addBreadcrumb: function() {},
        withScope: function(callback) { 
          callback({ 
            setTag: function() {}, 
            setExtra: function() {}, 
            setLevel: function() {}, 
            captureException: function() {} 
          }); 
        }
      };
      return;
    }
    
    var Sentry = window.Sentry;
    
    // ========================================================================
    var environment = enviroment.isDevelopment ? 'development' : 'production';
    // ========================================================================
    // INITIALIZE SENTRY (BOTH ENVIRONMENTS, BUT DIFFERENT CONFIGS)
    // ========================================================================
    
    var sentryConfig = {
      // DSN (Data Source Name) - unique identifier for your Sentry project
      dsn: "https://4a940b2fa6c580af9562b9de091074c5@o4509842006933504.ingest.de.sentry.io/4510827296260176",
      
      // Environment - automatically detected based on hostname
      environment: environment,
      
      // sendDefaultPii (Personally Identifiable Information)
      // When true, Sentry automatically collects IP addresses and user agent
      sendDefaultPii: true,
      
      // tracesSampleRate - controls performance monitoring
      tracesSampleRate: enviroment.isDevelopment ? 0 : 0.1,
    };
    
    if (enviroment.isDevelopment) {
      // ======================================================================
      // DEVELOPMENT: DISABLE ALL DEFAULT INTEGRATIONS
      // ======================================================================
      // This is the ONLY way to prevent function wrapping completely!
      // defaultIntegrations: false tells Sentry: "Don't load ANY integrations"
      // We use an empty array to ensure no integrations are loaded
      // ======================================================================
      sentryConfig.defaultIntegrations = false;
      sentryConfig.integrations = [];  // Empty array = no integrations at all
    } else {
      // ======================================================================
      // PRODUCTION: USE ALL DEFAULT INTEGRATIONS
      // ======================================================================
      sentryConfig.integrations = [
        Sentry.breadcrumbsIntegration({
          console: true,
          dom: true,
          fetch: true,
          history: true,
        }),
      ];
    }
    
    Sentry.init(sentryConfig);
    
    console.log('✅ Sentry initialized successfully');
    
    // ========================================================================
    // HELPER FUNCTION: GET USER CONTEXT FROM LOCALSTORAGE
    // ========================================================================
    // This function reads user data from localStorage directly
    // Used by global error handlers that don't have access to Angular's $injector
    // ========================================================================
    function getUserContextFromStorage() {
      try {
        var token = localStorage.getItem('userToken');
        if (!token) {
          return null; // Not authenticated
        }
        
        var userId = localStorage.getItem('userId');
        var userName = localStorage.getItem('userName');
        var userLastName = localStorage.getItem('userLastName');
        var roleId = localStorage.getItem('roleId');
        
        return {
          id: userId,
          username: (userName + ' ' + (userLastName || '')).trim(),
          role_id: roleId
        };
      } catch (e) {
        console.warn('Could not read user context from localStorage:', e);
        return null;
      }
    }
    
    // ========================================================================
    // GLOBAL ERROR HANDLER (for non-Angular errors)
    // ========================================================================
    // Catches JavaScript errors that occur outside Angular's execution context
    // Examples: setTimeout callbacks, third-party library errors, etc.
    // ========================================================================
    window.addEventListener('error', function(event) {
      if (window.Sentry) {
        // Set user context from localStorage
        var userContext = getUserContextFromStorage();
        if (userContext) {
          window.Sentry.setUser(userContext);
        }
        
        // Capture the error with context
        window.Sentry.withScope(function(scope) {
          scope.setTag('error_handler', 'global_window_error');
          scope.setExtra('filename', event.filename);
          scope.setExtra('lineno', event.lineno);
          scope.setExtra('colno', event.colno);
          
          // event.error contains the actual Error object
          scope.captureException(event.error || new Error(event.message));
        });
      }
    });
    
    // ========================================================================
    // UNHANDLED PROMISE REJECTION HANDLER
    // ========================================================================
    // Catches unhandled promise rejections (e.g., from native Promises, fetch)
    // ========================================================================
    window.addEventListener('unhandledrejection', function(event) {
      if (window.Sentry) {
        // Set user context from localStorage
        var userContext = getUserContextFromStorage();
        if (userContext) {
          window.Sentry.setUser(userContext);
        }
        
        // Capture the rejection with context
        window.Sentry.withScope(function(scope) {
          scope.setTag('error_handler', 'unhandled_promise_rejection');
          
          // event.reason contains the rejection value
          scope.captureException(event.reason);
        });
      }
    });
    
    console.log('✅ Global error handlers configured with user context support');
  },
  
  // ==========================================================================
  // SETUP ANGULAR INTEGRATION
  // ==========================================================================
  // Call this from app.js to integrate with Angular's error handling
  // This decorator automatically:
  // - Reads user from localStorage on every error
  // - Sends errors to Sentry with full context
  // - Preserves Angular's default error logging
  // ==========================================================================
  setupAngular: function(app) {
    
    // Use array notation for minification safety
    app.config(['$provide', function($provide) {
      
      // Decorate Angular's $exceptionHandler
      // Use array notation for minification safety
      $provide.decorator('$exceptionHandler', ['$delegate', '$injector', function($delegate, $injector) {
        
        return function(exception, cause) {
          
          // ==============================================================
          // SET SENTRY USER CONTEXT FROM LOCALSTORAGE
          // ==============================================================
          // Read user data from localStorage and set in Sentry
          // This ensures every error has user context, even after refresh
          // ==============================================================
          if (window.Sentry) {
            try {
              // Get authtokenService (use $injector to avoid circular dependency)
              var authtokenService = $injector.get('authtokenService');
              
              // Check if user is authenticated
              if (authtokenService.isAuthenticated()) {
                // Get user data from localStorage
                var userId = authtokenService.getUserId();
                var userName = authtokenService.getName();
                var userLastName = authtokenService.getLastName();
                var roleId = authtokenService.getRoleId();
                
                // Set user context in Sentry
                window.Sentry.setUser({
                  id: userId,
                  username: (userName + ' ' + userLastName).trim(),
                  role_id: roleId
                  // Note: We don't have email in localStorage
                  // If you add it, include it here
                });
              }
            } catch (e) {
              // If there's an error getting user context, don't break error reporting
              console.warn('Could not set Sentry user context:', e);
            }
          }
          
          // ==============================================================
          // SEND ERROR TO SENTRY
          // ==============================================================
          if (window.Sentry) {
            window.Sentry.withScope(function(scope) {
              // Add the 'cause' parameter as extra data
              if (cause) {
                scope.setExtra('cause', cause);
              }
              
              // Add Angular context
              scope.setTag('error_handler', 'angular_exception_handler');
              
              // Send the exception to Sentry
              scope.captureException(exception);
            });
          }
          
          // ==============================================================
          // CALL ANGULAR'S ORIGINAL ERROR HANDLER
          // ==============================================================
          // This ensures errors are still logged to console
          $delegate(exception, cause);
        };
      }]); // Close array notation
    }]); // Close array notation
    
    console.log('✅ Sentry Angular integration configured');
  }
  
};




//////////////////
// WEBPACK FOOTER
// ./public/development/assets/app/js/sentryIntegration.js
// module id = 159
// module chunks = 0